/**
 * Which user types the public signup page is allowed to offer.
 *
 * ZUPRIZE has had exactly one user type, "member", since the type was
 * configured, and the template leans on that: `getSoleUserTypeMaybe` in
 * SignupForm assigns the type automatically only while there is exactly one,
 * and `FieldSelectUserType` renders a dropdown as soon as there are two.
 *
 * So the day a second type is added in Console, two things break at once on
 * the public form: a user-type picker appears, and new accounts stop being
 * assigned "member" at all. Neither is wanted. Business accounts for the KBM
 * pilot are created through a private invite link, never chosen from a menu.
 *
 * Naming the invite-only types, rather than naming the public one, is what
 * keeps this quiet: another ordinary type added later is public by default,
 * and only the types listed here have to be let in deliberately.
 */

export const BUSINESS_USER_TYPE = 'business';

export const INVITE_ONLY_USER_TYPES = [BUSINESS_USER_TYPE];

/** The query parameter an invite link carries its token in: /signup/business?invite=… */
export const INVITE_QUERY_PARAM = 'invite';

/**
 * The user types a visitor may pick from, which is every configured type
 * except the invite-only ones.
 *
 * Filtering here also settles `/signup/:userType`: AuthenticationPage matches
 * the path parameter against this list, so `/signup/business` resolves to
 * nothing and 404s instead of quietly handing out a business account to
 * anyone who guesses the URL.
 *
 * @param {Array<{userType: string}>} userTypes - `config.user.userTypes`
 * @returns {Array<{userType: string}>}
 */
export const publicUserTypes = (userTypes = []) =>
  (userTypes || [])
    // A malformed entry would reach FieldSelectUserType and render an option
    // with no value; drop it here rather than let it become a broken choice.
    .filter(config => !!config?.userType)
    .filter(config => !INVITE_ONLY_USER_TYPES.includes(config.userType));

/**
 * Is this an invite-only type? For the places that will later accept a valid
 * invite token and let one through.
 *
 * @param {string} userType
 * @returns {boolean}
 */
export const isInviteOnlyUserType = userType => INVITE_ONLY_USER_TYPES.includes(userType);

/**
 * The invite token from a location's search string, or null.
 *
 * @param {string} search - `location.search`, with or without the leading '?'
 * @returns {string|null}
 */
export const readInviteToken = search => {
  if (!search || typeof search !== 'string') {
    return null;
  }
  const trimmed = search.startsWith('?') ? search.slice(1) : search;
  return new URLSearchParams(trimmed).get(INVITE_QUERY_PARAM) || null;
};

/**
 * Is this account a business seller? Read from the user type the account was
 * created with. This says what kind of account it is, not whether the
 * operator has verified it: anything that changes money or what other users
 * see must gate on `profile.metadata.businessVerified` instead.
 *
 * @param {Object} user - a user or currentUser entity
 * @returns {boolean}
 */
export const isBusinessUser = user =>
  user?.attributes?.profile?.publicData?.userType === BUSINESS_USER_TYPE;

/**
 * Is this seller a Zuprize Partner? A business account the operator has
 * verified in Console (`profile.metadata.businessVerified`, operator-only).
 * This is what the marking, the withdrawal notice and the VAT line gate on;
 * the server makes the same check before stamping VAT (server/api-util/vat.js).
 *
 * @param {Object} user - a user entity
 * @returns {boolean}
 */
export const isVerifiedBusiness = user =>
  isBusinessUser(user) && user?.attributes?.profile?.metadata?.businessVerified === true;

/**
 * The author fields a listing card needs to know whether its seller is a
 * Zuprize Partner. Queries that trim `fields.user` to what the card shows add
 * these, so the badge costs no extra request per card. Both are read-only to
 * the seller: the type is set at signup and the flag is operator metadata.
 */
export const PARTNER_USER_FIELDS = [
  'profile.publicData.userType',
  'profile.metadata.businessVerified',
  'profile.metadata.angreskjemaUrl',
];

/**
 * A Zuprize Partner's own withdrawal form: an address set by Zuprize in the
 * partner's operator-only metadata (`angreskjemaUrl`), either a full https
 * address or a path on this site such as
 * /static/documents/angrerettskjema-kirkens-bymisjon.pdf. Anything else is
 * ignored.
 *
 * @param {Object} user - a user entity
 * @returns {string|null}
 */
export const partnerAngreskjemaUrl = user => {
  const url = user?.attributes?.profile?.metadata?.angreskjemaUrl;
  return typeof url === 'string' && /^(https:\/\/|\/[^/])/.test(url.trim()) ? url.trim() : null;
};

/**
 * A business account's address, kept in three fields so each can be
 * required and the postcode checked, and shown as one line on the profile.
 */
export const BUSINESS_ADDRESS_KEYS = ['streetAddress', 'postalCode', 'city'];

/**
 * Where a seller ships from: the shipping address they saved, or, for a
 * business that hasn't saved one, the address it gave at sign-up, under the
 * company's name. Mirrors `senderAddressOf` in
 * server/api/shipping/address-utils.js, which books the shipment.
 *
 * @param {Object} user - the seller (currentUser on their own order page)
 * @returns {Object|null} `{ name?, addressLine, addressLine2?, postalCode, city }`, or null
 */
export const sellerShippingAddress = user => {
  const profile = user?.attributes?.profile || {};
  const saved = profile.privateData?.shippingAddress || null;
  if (saved?.addressLine && saved?.postalCode) {
    return saved;
  }
  const publicData = profile.publicData || {};
  const clean = key => (typeof publicData[key] === 'string' ? publicData[key].trim() : '');
  const [addressLine, postalCode, city] = BUSINESS_ADDRESS_KEYS.map(clean);
  if (!addressLine || !postalCode) {
    return saved;
  }
  return { name: clean('companyName') || undefined, addressLine, postalCode, city };
};

/**
 * "Storgata 1, 0155 Oslo" from a user's public data, or null when no part
 * of the address is set.
 *
 * @param {Object} publicData - `profile.publicData`
 * @returns {string|null}
 */
export const formatBusinessAddress = (publicData = {}) => {
  const clean = key => (typeof publicData?.[key] === 'string' ? publicData[key].trim() : '');
  const street = clean('streetAddress');
  const place = [clean('postalCode'), clean('city')].filter(Boolean).join(' ');
  const address = [street, place].filter(Boolean).join(', ');
  return address || null;
};
